import { useEffect, useMemo, useRef, useState } from 'react';
import { getPromotionClockOffsetMs } from '../../hooks/usePromotionClock';
import { getPromotionNextBoundaryMs } from '../../utils/priceListPromotions';

const MAX_TIMER_DELAY = 2147483647;

// Clock anchors belong to the response, so opening a preview later does not rewind time.
const useBranchDisplayClock = (display, onBoundary) => {
  const [tick, setTick] = useState(0);
  const callback = useRef(onBoundary);
  callback.current = onBoundary;
  const serverNow = display?.promotionClock?.serverNow;
  const receivedAt = display?.promotionClock?.receivedAt;
  const offset = useMemo(
    () => getPromotionClockOffsetMs(serverNow, receivedAt ?? Date.now()),
    [serverNow, receivedAt]
  );
  const now = Date.now() + offset;
  const promotions = (display?.items || []).flatMap((item) => item.promotions || []);
  const promotionBoundary = getPromotionNextBoundaryMs(promotions, now);
  const serverBoundary = Date.parse(display?.promotionClock?.nextBoundaryAt);
  const boundaries = [promotionBoundary, serverBoundary].filter((value) => Number.isFinite(value) && value > now);
  const boundary = boundaries.length ? Math.min(...boundaries) : null;

  useEffect(() => {
    if (boundary === null) return undefined;
    const timer = window.setTimeout(() => {
      setTick((value) => value + 1);
      if (Date.now() + offset >= boundary) callback.current?.();
    }, Math.min(Math.max(boundary - (Date.now() + offset) + 25, 0), MAX_TIMER_DELAY));
    return () => window.clearTimeout(timer);
  }, [boundary, offset, tick]);

  // Browsers may suspend timers while asleep. Reconcile before the next paint after returning.
  useEffect(() => {
    const wake = () => setTick((value) => value + 1);
    window.addEventListener('online', wake);
    document.addEventListener('visibilitychange', wake);
    return () => {
      window.removeEventListener('online', wake);
      document.removeEventListener('visibilitychange', wake);
    };
  }, []);

  return now;
};

export default useBranchDisplayClock;
